import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import type { DrawerPayloadAction } from 'types/utils.type';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import { errorNotification } from 'utils/notifications';
import {
  deleteEntityFromState,
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import {
  type EReportTemplateType,
  EReportingCapacitySource,
  EReportingTimeType,
  type TReportTemplate,
} from '@timeedit/types';
import api from '../services/api.service';

export const NEW_TEMP_ID = 'NEW-REPORT-TEMPLATE';

type TReportTemplateState = IDefaultAPIState<TReportTemplate> & {
  firstDrawer?: TReportTemplate;
  secondDrawer?: TReportTemplate;
};

export const initialState: TReportTemplateState = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
  firstDrawer: undefined,
  secondDrawer: undefined,
};

// Slice
const slice = createSlice({
  name: 'reportTemplate',
  initialState,
  reducers: {
    fetchDataRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchDataSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TReportTemplate>>
    ) => {
      state.results = payload.results;
      state.map = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchDataFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFieldsSetting: (
      state,
      { payload }: PayloadAction<Pick<TReportTemplate, 'id' | 'fieldSettings'>>
    ) => {
      const { id, fieldSettings } = payload;
      state.map[id] = {
        ...state.map[id],
        fieldSettings,
      };
    },
    createReportTemplate: (
      state,
      {
        payload: { templateType, timezone },
      }: PayloadAction<{ templateType: EReportTemplateType; timezone: string }>
    ) => {
      if (state.firstDrawer?.id === NEW_TEMP_ID) return;
      const newReportTemplate: TReportTemplate = {
        id: NEW_TEMP_ID,
        name: '',
        description: '',
        templateType,
        availableType: EReportingTimeType.FIXED_HOUR,
        capacitySource: EReportingCapacitySource.FROM_FIELD,
        timezone,
        targetObjectType: null,
      };
      state.firstDrawer = newReportTemplate;
      state.results.unshift(newReportTemplate);
      state.map[NEW_TEMP_ID] = newReportTemplate;
      finishedLoadingSuccess(state);
    },
    saveReportTemplateRequest: (state) => {
      isLoadingRequest(state);
    },
    saveReportTemplateSuccess: (
      state,
      {
        payload: { isFirstReportTemplate },
      }: PayloadAction<TSaveReportTemplateSuccessPayload>
    ) => {
      if (isFirstReportTemplate) {
        state.firstDrawer = undefined;
      } else {
        state.secondDrawer = undefined;
      }
      finishedLoadingSuccess(state);
    },
    saveReportTemplateFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteReportTemplateRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteReportTemplateSuccess: (
      state,
      {
        payload: { reportTemplateId },
      }: PayloadAction<{
        reportTemplateId: string;
      }>
    ) => {
      deleteEntityFromState(reportTemplateId, state);
      if (state.firstDrawer?.id === reportTemplateId) {
        state.firstDrawer = undefined;
      } else {
        state.secondDrawer = undefined;
      }
      finishedLoadingSuccess(state);
    },
    deleteReportTemplateFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstReportTemplate: (
      state,
      { payload }: PayloadAction<TReportTemplate>
    ) => {
      const previousFirstReportTemplate = state.firstDrawer;
      state.firstDrawer = payload;
      if (!payload && previousFirstReportTemplate?.id === NEW_TEMP_ID) {
        state.results = state.results.filter(
          (reportTemplate: TReportTemplate) => reportTemplate.id !== NEW_TEMP_ID
        );
      }
    },
    setSecondReportTemplate: (
      state,
      { payload }: PayloadAction<TReportTemplate>
    ) => {
      state.secondDrawer = payload;
    },
    duplicateReportTemplate: (
      state,
      { payload }: PayloadAction<TReportTemplate>
    ) => {
      if (state.firstDrawer?.id === NEW_TEMP_ID) return;
      const newReportTemplate: TReportTemplate = {
        ...payload,
        id: NEW_TEMP_ID,
        name: payload.name + '-copy',
      };
      state.firstDrawer = newReportTemplate;
      state.results.unshift(newReportTemplate);
      state.map[NEW_TEMP_ID] = newReportTemplate;
    },
    discardChanges: (
      state,
      {
        payload,
      }: PayloadAction<{
        id: string;
        isFirstDrawer: boolean;
      }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = state.map[id];
      } else {
        state.secondDrawer = state.map[id];
      }
    },
    changeReportTemplate: <Key extends keyof TReportTemplate>(
      state: TReportTemplateState,
      { payload }: DrawerPayloadAction<TReportTemplate, Key>
    ) => {
      const { isFirstDrawer, key, value } = payload;
      if (isFirstDrawer) {
        state.firstDrawer = {
          ...state.firstDrawer,
          [key]: value,
        };
      } else {
        state.secondDrawer = {
          ...state.secondDrawer,
          [key]: value,
        };
      }
    },
  },
});

export default slice.reducer;

// Selectors
export const reportTemplatesLoading = (state: TRootState) =>
  state.reportTemplate.loading;
export const reportTemplatesSelector = (state: TRootState) =>
  state.reportTemplate.results;
export const reportTemplateMapSelector = (state: TRootState) =>
  state.reportTemplate.map;
export const firstReportTemplateSelector = (state: TRootState) =>
  state.reportTemplate.firstDrawer;
export const secondReportTemplateSelector = (state: TRootState) =>
  state.reportTemplate.secondDrawer;

// TODO: Replace this with a `selectedIds` property updated when firstDrawer or secondDrawer is updated
export const selectedIdsSelector = (state: TRootState) =>
  _.compact([
    state.reportTemplate.firstDrawer,
    state.reportTemplate.secondDrawer,
  ]).map((pr) => pr.id);

// Actions
export const {
  fetchDataRequest,
  fetchDataSuccess,
  fetchDataFailure,
  createReportTemplate,
  deleteReportTemplateRequest,
  deleteReportTemplateFailure,
  deleteReportTemplateSuccess,
  saveReportTemplateRequest,
  saveReportTemplateSuccess,
  saveReportTemplateFailure,
  setFirstReportTemplate,
  setSecondReportTemplate,
  duplicateReportTemplate,
  discardChanges,
  changeReportTemplate,
  setFieldsSetting,
} = slice.actions;

export const fetchTemplate = createAppAsyncThunk<void>(
  'reportTemplate/fetchTemplate',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchDataRequest());
      const result = await api.get({
        endpoint: '/reporting/template-settings',
        suppressNotification: true,
      });
      dispatch(fetchDataSuccess(result));
    } catch (e) {
      dispatch(fetchDataFailure());
      errorNotification('Failed to fetch report templates');
    }
  }
);

type TSaveReportTemplatePayload = TSaveReportTemplateSuccessPayload & {
  reportTemplate: TReportTemplate;
};

type TSaveReportTemplateSuccessPayload = {
  isFirstReportTemplate: boolean;
};

export const saveReportTemplate = createAppAsyncThunk<
  void,
  TSaveReportTemplatePayload
>(
  'reportTemplate/saveReportTemplate',

  async ({ isFirstReportTemplate, reportTemplate }, { dispatch }) => {
    try {
      dispatch(saveReportTemplateRequest());
      /**
       * Could be either create or update
       * Depending on which, we need to use different methods (POST or PATCH)
       */
      const { id, ...body } = reportTemplate;

      const sizeSpans = [...(body.demandSizeSpans || [])];
      body.demandSizeSpans = sizeSpans.sort((a, b) => a[0] - b[0]);

      if (id === NEW_TEMP_ID) {
        // Create a new one
        await api.post({
          endpoint: `/reporting/template-settings`,
          data: body,
        });
        dispatch(saveReportTemplateSuccess({ isFirstReportTemplate }));
        dispatch(fetchTemplate());
      } else {
        await api.patch({
          endpoint: `/reporting/template-settings/${reportTemplate.id}`,
          data: body,
        });
        dispatch(saveReportTemplateSuccess({ isFirstReportTemplate }));
        dispatch(fetchTemplate());
      }
    } catch (error) {
      dispatch(saveReportTemplateFailure());
      console.error(error);
    }
  }
);

export const deleteReportTemplate = createAppAsyncThunk<void, string>(
  'reportTemplate/deleteReportTemplate',
  async (reportTemplateId, { dispatch }) => {
    try {
      dispatch(deleteReportTemplateRequest());
      /**
       * No need to send the request to the backend if the entity hasn't been saved
       */
      if (reportTemplateId !== NEW_TEMP_ID)
        await api.delete({
          endpoint: `/reporting/template-settings/${reportTemplateId}`,
        });
      dispatch(deleteReportTemplateSuccess({ reportTemplateId }));
    } catch (e) {
      dispatch(deleteReportTemplateFailure());
      console.error(e);
    }
  }
);

/**
 * Travel time padding rule has distances array, each of which has fieldValues array.
 * The order of array are changed after matrix component, so we need to sort them before comparing.
 **/
const checkReportTemplateChanged = (
  reportTemplate1: TReportTemplate,
  reportTemplate2: TReportTemplate
): boolean => {
  return checkSavedDisabled(reportTemplate1, reportTemplate2);
};

export const checkSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { reportTemplate } = getState();
    const {
      firstDrawer,
      secondDrawer,
      map: reportTemplateMap,
    } = reportTemplate;
    if (isFirstDrawer && firstDrawer && reportTemplateMap[firstDrawer.id]) {
      return checkReportTemplateChanged(
        firstDrawer,
        reportTemplateMap[firstDrawer.id]
      );
    }
    if (!isFirstDrawer && secondDrawer && reportTemplateMap[secondDrawer.id]) {
      return checkReportTemplateChanged(
        secondDrawer,
        reportTemplateMap[secondDrawer.id]
      );
    }

    return true;
  };

export const checkFirstReportTemplateExist =
  (): TAppThunk<boolean> => (_, getState) =>
    !!getState().reportTemplate.firstDrawer;
